import * as React from "react";
import { DateTimeAccuracy, LocalizedDateTime } from "../../../misc/components/LocalizedDateTime";
import { CommentModals } from "../../Shared/CommentsModals";
import { FixedCommentsSelectors } from "../../Shared/FixedCommentsSelectors";
import { PFTResults, PressureResults, StudyRelevantParameters } from "../../Shared/PFTAPITypes";
import { PFTResultParameters } from "../../Shared/PFTResultsParameters";
import RepeatabilityTable from "../../Shared/RepeatabilityTable";
import { SelectedSessionCommentsCount, SelectedTestCommentsCount } from "../../Shared/Selectors/FixedCommentSelectors";
import { Grades, TestAcceptabilityTable } from "../../Shared/TestAcceptabilityTable";
import { AcceptabilityState, ToggleTestAcceptabilityAction } from "../../Shared/UseAcceptability";
import { FixedCommentsState, LoadAnalysisSetOverreadAction, PageValidatedAction, ToggleSessionFixedCommentsVisibilityAction, ToggleTestFixedCommentsVisibilityAction } from "../../Shared/UseFixedComments";
import { QualityAcceptabilityTable } from "../../Shared/QualityAcceptabilityTable";
import { GraphState, ToggleTestGraphVisibilityAction } from "../UsePressureGraph";
import { useWebPortalLegacyClient } from "../../../misc/api/WebPortalTokenClient";

export interface MepResultsTableProps {
    isViewOnly: boolean;
    isRejected: boolean;
    isSiteAnalysisSet: boolean;
    resultData: PressureResults;
    fixedCommentsState: FixedCommentsState;
    fixedCommentsDispatch;
    acceptabilityState: AcceptabilityState;
    acceptabilityDispatch;
    shouldValidateFixedComments: boolean;
    grades: Grades;
    studyMepRelevantParameters?: StudyRelevantParameters;
    graphState: GraphState;
    graphDispatch;
}

export const MepResultsTable = (props: MepResultsTableProps) => {
    const legacyClient = useWebPortalLegacyClient();

    React.useEffect(() => {
        const fetchOverreading = async () => {
            const analysisOverreading = await legacyClient.getMepAnalysisSetOverreading(props.resultData.AnalysisSetId);

            props.fixedCommentsDispatch(LoadAnalysisSetOverreadAction.create(analysisOverreading));
        }

        fetchOverreading();
    }, [props.resultData.AnalysisSetId, props.fixedCommentsDispatch])

    return (
        <>
            <table id="MepResultsTable" className="table table-striped">
                <thead>
                    <tr>
                        <th></th>
                        {
                            props.resultData.TestResults.sort((a, b) => a.TestNumber - b.TestNumber).map(t =>
                                <th key={`TestHeader${t.TestId}`} style={{ color: "#000" }}>{t.TestNumber}</th>
                            )
                        }
                        <th>Best</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>Display</td>
                        {props.graphState.flow.Tests.sort((a, b) => a.TestNumber - b.TestNumber).map(t => (
                            <td key={`TestVisibilityToggle${t.TestId}`}>
                                <input
                                    checked={t.IsDisplayed}
                                    type="checkbox"
                                    onChange={() =>
                                        props.graphDispatch(
                                            ToggleTestGraphVisibilityAction.create(t.TestId)
                                        )
                                    }
                                />
                            </td>
                        ))}
                        <td />
                    </tr>
                    <tr>
                        <td>Time</td>
                        {
                            props.resultData.TestResults.sort((a, b) => a.TestNumber - b.TestNumber).map(t =>
                                <td key={`TestDateTime${t.TestId}`}>
                                    <LocalizedDateTime time={t.TestDateTime} accuracy={DateTimeAccuracy.Second} />
                                </td>
                            )
                        }
                        <td></td>
                    </tr>

                    <PFTResultParameters
                        isLoading={props.acceptabilityState.loadingBestValues}
                        results={props.resultData}
                        isRejected={props.isRejected}
                        studyRelevantParameters={props.studyMepRelevantParameters}
                        showConfiguredParametersOnly={true}
                    />

                    <RepeatabilityTable
                        isLoading={props.acceptabilityState.loadingBestValues}
                        results={props.resultData}
                        isRejected={props.isRejected}
                        studyRelevantParameters={props.studyMepRelevantParameters}
                        showConfiguredParametersOnly={true}
                        pressureSessionType="MEP"
                    />

                    <QualityAcceptabilityTable results={props.resultData} />

                    <TestAcceptabilityTable
                        isViewOnly={props.isViewOnly}
                        isLoading={props.acceptabilityState.loadingBestValues}
                        testResults={props.acceptabilityState.spirometryResults.TestResults}
                        grades={props.grades}
                        displayTechnicianAcceptability={!props.isSiteAnalysisSet}
                        onAcceptabilityChange={(id, checked) =>
                            props.acceptabilityDispatch(
                                ToggleTestAcceptabilityAction.create(id, checked)
                            )
                        }
                    />

                    <FixedCommentsSelectors
                        testResults={props.resultData.TestResults}
                        selectedTestCommentsCount={SelectedTestCommentsCount(props.fixedCommentsState.testFixedComments)}
                        selectedSessionCommentsCount={
                            SelectedSessionCommentsCount(props.fixedCommentsState.sessionFixedComments)
                        }
                        onTestFixedCommentClick={testId =>
                            props.fixedCommentsDispatch(
                                ToggleTestFixedCommentsVisibilityAction.create(testId)
                            )
                        }
                        onSessionFixedCommentClick={() =>
                            props.fixedCommentsDispatch(
                                ToggleSessionFixedCommentsVisibilityAction.create()
                            )
                        }
                        addCommentsSuffix={true}
                    />

                </tbody>
            </table>
            <CommentModals
                editable={!props.isViewOnly}
                sessionFixedComments={props.fixedCommentsState.sessionFixedComments}
                testFixedComments={props.fixedCommentsState.testFixedComments}
                setIsPageValidated={(isValidated) => props.fixedCommentsDispatch(PageValidatedAction.create(isValidated))}
                isPageValid={props.fixedCommentsState.isPageValidated}
                dispatch={props.fixedCommentsDispatch}
            />
        </>
    );
}
